import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Target, TrendingUp, TrendingDown, AlertTriangle, Download, FileText } from "lucide-react";
import html2pdf from "html2pdf.js";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";
import { YearlyReportTable } from "./YearlyReportTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => v == null ? "-" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? "-" : `${v.toFixed(1)}%`;
const fmtNum = (v) => v == null ? "-" : new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(v);

const KPI = ({ label, value, sub, color = "slate" }) => (
  <div className={`p-4 rounded-lg border bg-${color}-50 border-${color}-200`}>
    <div className="text-xs text-muted-foreground mb-1">{label}</div>
    <div className="text-xl font-bold">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{fmtEur(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export const BreakEvenPage = ({ clientId, clientName }) => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [showPrevYear, setShowPrevYear] = useState(false);
  const [showPrevCE, setShowPrevCE] = useState(false);

  useEffect(() => { fetchData(); }, [clientId, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/break-even`, { params: { year } });
      setData(res.data);
    } catch (e) { toast.error("Errore nel caricamento BEP"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Caricamento Break Even...</div>;
  if (!data) return null;
  if (!data.actual_months.length) return (
    <Card><CardContent className="py-12 text-center">
      <Target className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
      <p className="text-lg font-semibold">Nessun dato per il {year}</p>
      <p className="text-muted-foreground">Carica i bilanci mensili per vedere il Break Even Point</p>
    </CardContent></Card>
  );

  const { actual_months, projected_months, ytd, full_year_projection, bep_chart, has_projection, projection_info, has_prev_year_pattern, prev_year_data } = data;
  const isAboveBEP = ytd.margin_of_safety > 0;

  // Monthly trend data
  const trendData = [...actual_months, ...projected_months].map(m => ({
    name: m.month_name,
    type: m.type,
    Ricavi: m.revenue,
    "BEP (Ricavi)": m.bep_revenue,
    "Costi Fissi": m.fixed_costs,
  }));

  // Margin of safety monthly
  const safetyData = [...actual_months, ...projected_months].map(m => ({
    name: m.month_name,
    type: m.type,
    "Margine di Sicurezza": m.margin_of_safety,
    "MDC %": m.mdc_pct,
  }));

  // BEP Chart: Cumulative MDC vs Projected Annual Fixed Costs
  const allMonths = [...actual_months, ...projected_months];

  const MONTHS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  // Helper: build chart data on a numeric X axis (month index 1..12 + fractional crossing)
  // so that the BEP reference line sits exactly at the visual intersection
  const buildBepSeries = (months, annualFC) => {
    const result = [];
    let cumMDC = 0;
    let crossingAdded = false;
    let crossingX = null;
    let crossingMonthName = null;
    for (let i = 0; i < months.length; i++) {
      const m = months[i];
      const prevCum = cumMDC;
      const mdc = m.revenue - m.variable_costs;
      cumMDC += mdc;
      const prevX = i === 0 ? (m.month - 1) : (months[i - 1].month);
      const curX = m.month;
      // Detect crossing between prev point and this point
      if (!crossingAdded && prevCum < annualFC && cumMDC >= annualFC && mdc > 0) {
        const frac = (annualFC - prevCum) / (cumMDC - prevCum);
        crossingX = prevX + (curX - prevX) * frac;
        crossingMonthName = m.month_name;
        result.push({
          x: crossingX,
          name: crossingMonthName,
          type: m.type === 'projected' ? 'projected' : 'actual',
          isCrossing: true,
          "MDC Cumulato": Math.round(annualFC),
          "Costi Fissi Annui": Math.round(annualFC),
        });
        crossingAdded = true;
      }
      result.push({
        x: curX,
        name: m.month_name,
        type: m.type || 'actual',
        "MDC Cumulato": Math.round(cumMDC),
        "Costi Fissi Annui": Math.round(annualFC),
      });
    }
    return { data: result, annualFC, crossingX, crossingMonthName };
  };

  const bepChartData = (() => {
    const actualFC = actual_months.reduce((s, m) => s + m.fixed_costs, 0);
    const avgMonthlyFC = actual_months.length > 0 ? actualFC / actual_months.length : 0;
    const annualFC = Math.round(avgMonthlyFC * 12);
    return buildBepSeries(allMonths, annualFC);
  })();

  const bepMonthName = bepChartData.crossingMonthName;
  const bepCrossingX = bepChartData.crossingX;

  // BEP Chart for PREVIOUS YEAR (if available)
  const prevBepChartData = (() => {
    if (!prev_year_data || !prev_year_data.actual_months?.length) return null;
    const pm = prev_year_data.actual_months;
    const pyActualFC = pm.reduce((s, m) => s + m.fixed_costs, 0);
    const pyAvgMonthlyFC = pm.length > 0 ? pyActualFC / pm.length : 0;
    const pyAnnualFC = Math.round(pyAvgMonthlyFC * 12);
    return buildBepSeries(pm, pyAnnualFC);
  })();

  const prevBepMonthName = prevBepChartData?.crossingMonthName || null;
  const prevBepCrossingX = prevBepChartData?.crossingX ?? null;

  const exportPDF = () => {
    const element = document.querySelector('[data-testid="break-even-page"]');
    if (!element) return;
    html2pdf().set({
      margin: [10, 5, 10, 5],
      filename: `BreakEven_${year}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(element).save();
    toast.success("PDF in generazione...");
  };

  return (
    <div className="space-y-6" data-testid="break-even-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Target className="h-5 w-5" /> Break Even Point
          </h3>
          <p className="text-sm text-muted-foreground">
            Analisi del punto di pareggio
            {has_projection && has_prev_year_pattern && ` con proiezione basata sull'andamento ${year - 1}`}
            {has_projection && !has_prev_year_pattern && " con proiezione piatta (nessun dato anno precedente)"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {prev_year_data && (
            <div className="flex items-center gap-2 border rounded-md px-2 py-1 bg-slate-50">
              <Switch id="toggle-prev-year" checked={showPrevYear} onCheckedChange={setShowPrevYear} data-testid="toggle-prev-year-switch" />
              <Label htmlFor="toggle-prev-year" className="text-xs cursor-pointer">Confronto {year - 1}</Label>
            </div>
          )}
          {prev_year_data && (
            <Button variant="outline" size="sm" onClick={() => setShowPrevCE(true)} data-testid="open-prev-ce-btn">
              <FileText className="h-4 w-4 mr-1" /> Riclassificato {year - 1}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-bep-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Label className="text-sm">Anno:</Label>
          <Select value={year.toString()} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI
          label="BEP Progressivo (YTD)"
          value={fmtEur(ytd.bep_revenue)}
          sub={`Ricavi attuali: ${fmtEur(ytd.revenue)}`}
          color={isAboveBEP ? "emerald" : "red"}
        />
        <KPI
          label="Margine di Sicurezza"
          value={fmtEur(ytd.margin_of_safety)}
          sub={fmtPct(ytd.margin_of_safety_pct)}
          color={isAboveBEP ? "emerald" : "red"}
        />
        <KPI
          label="MDC % YTD (applicata alla proiezione)"
          value={fmtPct(ytd.mdc_pct)}
          sub="Margine di contribuzione effettivo"
        />
        {has_projection && (
          <KPI
            label="BEP Proiezione Anno"
            value={fmtEur(full_year_projection.bep_revenue)}
            sub={`Ricavi previsti: ${fmtEur(full_year_projection.revenue)}`}
            color={full_year_projection.margin_of_safety > 0 ? "blue" : "amber"}
          />
        )}
      </div>

      {/* Projection method info */}
      {has_projection && projection_info && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <div className="font-semibold mb-1">
            {has_prev_year_pattern
              ? `Proiezione basata sull'andamento ${year - 1} (${projection_info.prev_year_months_available} mesi disponibili)`
              : "Proiezione piatta (nessun dato anno precedente)"}
          </div>
          <div className="text-xs text-blue-600">
            {has_prev_year_pattern
              ? `I ricavi futuri seguono le variazioni mese-su-mese del ${year - 1}. I costi variabili usano la MDC% YTD reale (${fmtPct(projection_info.ytd_mdc_pct_applied)}). Man mano che carichi nuovi mesi, la proiezione si aggiorna automaticamente.`
              : "I ricavi futuri sono stimati come media dei mesi gia' caricati. Carica i dati dell'anno precedente per una proiezione piu' accurata."}
          </div>
        </div>
      )}

      {/* BEP Chart - Cumulative MDC vs Annual Fixed Costs */}
      {bepChartData.data.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Grafico Break Even Point
              {bepMonthName ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs ml-2">
                  BEP raggiunto a {bepMonthName}
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 border-red-300 text-xs ml-2">
                  BEP non raggiunto nell'anno
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Il margine di contribuzione cumulato cresce mese per mese fino a coprire i costi fissi annui proiettati ({fmtEur(bepChartData.annualFC)})
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={bepChartData.data} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradMDC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[1, 12]}
                  ticks={[1,2,3,4,5,6,7,8,9,10,11,12]}
                  tickFormatter={(v) => MONTHS_IT[Math.round(v) - 1] || ''}
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                />
                <YAxis tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} />
                <RTooltip content={<CustomTooltip />} labelFormatter={(v, payload) => {
                  const p = payload && payload[0] && payload[0].payload;
                  if (p?.isCrossing) return `BEP raggiunto (${p.name})`;
                  return p?.name || MONTHS_IT[Math.round(v) - 1] || v;
                }} />
                <Legend />
                <Line type="monotone" dataKey="Costi Fissi Annui" stroke="#ef4444" strokeWidth={2.5} strokeDasharray="8 4" dot={false} />
                <Area type="monotone" dataKey="MDC Cumulato" stroke="#22c55e" fill="url(#gradMDC)" strokeWidth={2.5} dot={({ cx, cy, index }) => {
                  const d = bepChartData.data[index];
                  if (d?.isCrossing) return null;
                  const isPast = d?.type !== 'projected';
                  return <circle cx={cx} cy={cy} r={isPast ? 5 : 3} fill={isPast ? '#22c55e' : '#86efac'} stroke="#fff" strokeWidth={2} />;
                }} />
                {bepMonthName && (
                  <ReferenceLine x={bepCrossingX} stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" label={{ value: "BEP", position: "top", fill: "#f59e0b", fontWeight: "bold", fontSize: 13 }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* PREVIOUS YEAR BEP Chart (toggle) */}
      {showPrevYear && prevBepChartData && (
        <Card className="border-slate-300 bg-slate-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-slate-600">Grafico Break Even Point — {year - 1}</span>
              {prevBepMonthName ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs ml-2">
                  BEP raggiunto a {prevBepMonthName}
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 border-red-300 text-xs ml-2">
                  BEP non raggiunto
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Dati storici anno {year - 1}: {prev_year_data.months_count} mesi. Costi fissi annui proiettati: {fmtEur(prevBepChartData.annualFC)}. Ricavi YTD: {fmtEur(prev_year_data.ytd.revenue)} · MDC%: {fmtPct(prev_year_data.ytd.mdc_pct)} · BEP YTD: {fmtEur(prev_year_data.ytd.bep_revenue)}.
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={prevBepChartData.data} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradMDCPrev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[1, 12]}
                  ticks={[1,2,3,4,5,6,7,8,9,10,11,12]}
                  tickFormatter={(v) => MONTHS_IT[Math.round(v) - 1] || ''}
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                />
                <YAxis tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} />
                <RTooltip content={<CustomTooltip />} labelFormatter={(v, payload) => {
                  const p = payload && payload[0] && payload[0].payload;
                  if (p?.isCrossing) return `BEP raggiunto (${p.name})`;
                  return p?.name || MONTHS_IT[Math.round(v) - 1] || v;
                }} />
                <Legend />
                <Line type="monotone" dataKey="Costi Fissi Annui" stroke="#ef4444" strokeWidth={2} strokeDasharray="8 4" dot={false} />
                <Area type="monotone" dataKey="MDC Cumulato" stroke="#6366f1" fill="url(#gradMDCPrev)" strokeWidth={2.5} />
                {prevBepMonthName && (
                  <ReferenceLine x={prevBepCrossingX} stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" label={{ value: "BEP", position: "top", fill: "#f59e0b", fontWeight: "bold", fontSize: 13 }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly BEP Trend + Projection */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Trend Mensile BEP {has_projection && " + Proiezione"}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Confronto ricavi mensili vs BEP mensile
            {has_projection && has_prev_year_pattern && ` — Proiezione: andamento ${year - 1} + MDC% YTD ${fmtPct(ytd.mdc_pct)}`}
            {has_projection && !has_prev_year_pattern && " — Proiezione piatta da media mesi caricati"}
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} />
              <RTooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="Ricavi" fill="#22c55e" radius={[4, 4, 0, 0]} opacity={0.8} />
              <Bar dataKey="BEP (Ricavi)" fill="#f59e0b" radius={[4, 4, 0, 0]} opacity={0.6} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margin of Safety Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Margine di Sicurezza Mensile</CardTitle>
          <p className="text-xs text-muted-foreground">
            Differenza tra ricavi e BEP. Positivo = sopra il pareggio.
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={safetyData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} />
              <RTooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={2} />
              <Bar dataKey="Margine di Sicurezza" radius={[4, 4, 0, 0]}>
                {safetyData.map((entry, i) => (
                  <rect key={i} fill={entry["Margine di Sicurezza"] >= 0 ? "#22c55e" : "#ef4444"} opacity={entry.type === "projected" ? 0.4 : 0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Detail Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="bep-table">
              <thead>
                <tr className="bg-slate-100 border-b-2">
                  <th className="text-left px-3 py-2">Mese</th>
                  <th className="text-right px-3 py-2">Ricavi</th>
                  <th className="text-right px-3 py-2">Crescita</th>
                  <th className="text-right px-3 py-2">Costi Var.</th>
                  <th className="text-right px-3 py-2">MDC</th>
                  <th className="text-right px-3 py-2">MDC %</th>
                  <th className="text-right px-3 py-2">Costi Fissi</th>
                  <th className="text-right px-3 py-2 bg-amber-50">BEP</th>
                  <th className="text-right px-3 py-2 bg-emerald-50">Margine Sic.</th>
                </tr>
              </thead>
              <tbody>
                {[...actual_months, ...projected_months].map((m, idx) => {
                  const allMonths = [...actual_months, ...projected_months];
                  const prevMonth = idx > 0 ? allMonths[idx - 1] : null;
                  const momGrowth = prevMonth && prevMonth.revenue > 0
                    ? ((m.revenue - prevMonth.revenue) / prevMonth.revenue * 100)
                    : null;

                  return (
                    <tr key={m.month} className={`border-b ${m.type === 'projected' ? 'bg-blue-50/30 italic' : ''}`}>
                      <td className="px-3 py-1.5 font-medium">
                        {m.month_name}
                        {m.type === 'projected' && <Badge variant="secondary" className="ml-2 text-[10px]">proiezione</Badge>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(m.revenue)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                        {momGrowth != null ? (
                          <span className={momGrowth >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                            {momGrowth >= 0 ? '+' : ''}{momGrowth.toFixed(1)}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(m.variable_costs)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(m.margin_contribution)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtPct(m.mdc_pct)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(m.fixed_costs)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums bg-amber-50 font-semibold">{fmtEur(m.bep_revenue)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${m.margin_of_safety >= 0 ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
                        {fmtEur(m.margin_of_safety)}
                      </td>
                    </tr>
                  );
                })}
                {/* YTD Row */}
                <tr className="bg-slate-800 text-white font-bold border-t-2">
                  <td className="px-3 py-2">PROGRESSIVO YTD</td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.revenue)}</td>
                  <td className="px-3 py-2 text-right"></td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.variable_costs)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.mdc)}</td>
                  <td className="px-3 py-2 text-right">{fmtPct(ytd.mdc_pct)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.fixed_costs)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.bep_revenue)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(ytd.margin_of_safety)}</td>
                </tr>
                {has_projection && (
                  <tr className="bg-blue-800 text-white font-bold">
                    <td className="px-3 py-2">PROIEZIONE ANNO</td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.revenue)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.variable_costs)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.mdc)}</td>
                    <td className="px-3 py-2 text-right">{fmtPct(full_year_projection.mdc_pct)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.fixed_costs)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.bep_revenue)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(full_year_projection.margin_of_safety)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sheet: Riclassificato CE anno precedente */}
      <Sheet open={showPrevCE} onOpenChange={setShowPrevCE}>
        <SheetContent side="right" className="w-full sm:max-w-[95vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Riclassificato CE {year - 1} — {clientName}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <YearlyReportTable clientId={clientId} clientName={clientName} initialYear={year - 1} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BreakEvenPage;
